import type * as Api from '@ViewModels';
import { StepSendFromOption } from '../../../components/automation/AutomationStepCard/components/StepSendFromOption';
import { StepAdvancedOptionsPanel } from './StepAdvancedOptionsPanel';
import type { IActionItemAutomationStepExtended } from '../types/automationSteps';

/**
 * ActionItemAdvancedOptions component.
 *
 * Displays collapsible advanced options for action item steps including:
 * - Send from options (assignment)
 * - Conditional contact filters (skip criteria)
 *
 * @param actionItemStep - The action item step data
 * @param nodeId - The node identifier
 * @param stepId - The step identifier
 * @param skipStepContactFilter - Current contact filter criteria
 * @param canEdit - Whether the step can be edited
 * @param isCollapsed - Whether the advanced options are collapsed
 * @param onToggle - Handler for toggling collapsed state
 * @param onSaveSenderOptions - Handler for saving sender options
 * @param onFilterChange - Handler for filter changes
 */
interface ActionItemAdvancedOptionsProps {
	actionItemStep: IActionItemAutomationStepExtended | null;
	nodeId: string;
	stepId: string;
	skipStepContactFilter: Api.IContactsFilterRequest | null | undefined;
	canEdit: boolean;
	isCollapsed: boolean;
	onToggle: () => void;
	onSaveSenderOptions: (option: Api.ISendFromOptions | null, sendToSender?: boolean) => void;
	onFilterChange: (filter: Api.IContactsFilterRequest | null) => void;
	connectionTypes?: Api.IConnectionType[];
	impersonationContext?: Api.IImpersonationContext;
}

export function ActionItemAdvancedOptions({
	actionItemStep,
	nodeId: _nodeId,
	stepId,
	skipStepContactFilter,
	canEdit,
	isCollapsed,
	onToggle,
	onSaveSenderOptions,
	onFilterChange,
	connectionTypes,
	impersonationContext,
}: ActionItemAdvancedOptionsProps) {
	return (
		<StepAdvancedOptionsPanel
			isExpanded={!isCollapsed}
			onToggle={onToggle}
			stepId={stepId}
			filter={skipStepContactFilter}
			canEdit={canEdit}
			isBusy={actionItemStep?.isBusy}
			onFilterChange={onFilterChange}
		>
			{actionItemStep && (
				<StepSendFromOption
					step={actionItemStep}
					onSave={onSaveSenderOptions}
					connectionTypes={connectionTypes}
					sendFromLabel={'Always assign this action item to'}
					canEdit={canEdit}
					impersonationContext={impersonationContext}
				/>
			)}
		</StepAdvancedOptionsPanel>
	);
}
